import clsx from "clsx";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  NextTask,
  PROGRESS_NOTIFICATION_TYPE_COMPLETION_REPORT,
  ProgressNotification,
  ProgressNotificationFetchedNotificationLargestId,
  ProgressNotificationType,
} from "../../models";

import DetailRow from "./DetailRow";
import Header from "./Header";

import { Spacer, SubmitButton } from "@/components/common";

type Props = {
  notification: ProgressNotification;
  onDetailPress: () => void;
  fetchedNotificationLargestIdBeforeOpen: ProgressNotificationFetchedNotificationLargestId;
  notificationType: ProgressNotificationType;
  onUpdateNextTask: (nextTask: NextTask) => void;
};

const NotificationRow: React.FC<Props> = ({
  notification,
  fetchedNotificationLargestIdBeforeOpen,
  notificationType,
  onUpdateNextTask,
  ...props
}) => {
  const { t } = useTranslation();

  const nextTask = notification.next_task;

  const isNew = useMemo(() => {
    return notification.progress_notification_id > fetchedNotificationLargestIdBeforeOpen[notificationType];
  }, [notification, fetchedNotificationLargestIdBeforeOpen]);

  const onDetailPress = () => {
    props.onDetailPress();
  };

  return (
    <div className={clsx("progress-notification", isNew ? "is-new" : "bg-[#fff]")}>
      <Header
        title={notification.notification_type_name}
        timestamp={notification.notification_date}
        onClickDetail={onDetailPress}
      />
      <div className="progress-notification-content">
        {Object.entries(notification.information).map(([key, value], index) => (
          <DetailRow
            key={`notification-${notification.progress_notification_id}-detail-${index}`}
            label={key}
            content={value}
            comment_num={notification.comment_num}
          />
        ))}
      </div>
      {/* 次作業 */}
      {notificationType === PROGRESS_NOTIFICATION_TYPE_COMPLETION_REPORT && nextTask?.task_id && (
        // 削除された次作業（nextTask.status = null）は枠だけ表示して情報は空にする
        <div className="mx-[20px] my-[5px] border-solid border-[#cccccc] border-[1px] rounded-[5px] overflow-hidden">
          <div className={clsx("p-[3px] truncate", nextTask.status ? "bg-[#cccccc]" : "bg-[#eeeeff]")}>
            {t("next_task")}：{nextTask.status === null ? t("deleted") : nextTask.process_name}
          </div>
          {nextTask.status === null ? (
            <div className="p-[3px] h-[26px] bg-[#fff]" />
          ) : (
            <div className={clsx("p-[3px]", nextTask.status ? "bg-[#dddddd]" : "bg-[#fff]")}>
              <div className="flex">
                <div className="w-[60px] flex justify-end items-center">
                  {!nextTask.display_status ? (
                    <span className="inline-block align-middle w-[12px] h-[12px] border border-dotted border-gray-500" />
                  ) : nextTask.display_status === "-" ? (
                    "ー"
                  ) : (
                    nextTask.display_status
                  )}
                </div>
                {nextTask.roles?.[0] ? (
                  <>
                    <span className="w-[20px] whitespace-nowrap">&nbsp;→&nbsp;</span>
                    <SubmitButton
                      style={{ height: "24px", padding: "0 7px", fontSize: "13px" }}
                      onClick={() => onUpdateNextTask(nextTask)}
                    >
                      {nextTask.status ? t("revert_task_to_incomplete") : t("make_task_complete")}
                    </SubmitButton>
                  </>
                ) : (
                  <></>
                )}
              </div>
              <Spacer y="3px" />
              <div className="flex">
                <div className="w-[80px] text-right">{t("completed_by")}：&nbsp;</div>
                <span>{nextTask.completion_user_name}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationRow;
